import { useRef, useState } from 'react';
import { useI18nContext } from '@/i18n/i18n-react';
import {
  getSnapHeights,
  nearestSnap,
  nextSnapOnHandleTap,
  stepSnap,
  type SheetSnap,
} from '@/lib/app-editor/editor-sheet';
import { cn } from '@/lib/utils';

export interface EditorSheetProps {
  snap: SheetSnap;
  onSnapChange: (snap: SheetSnap) => void;
  /** Height available to the editor body (measured by the layout). */
  bodyHeight: number;
  /** "Ativo" row + the tab list. */
  header: React.ReactNode;
  /** Tab panels (scrolls). */
  children: React.ReactNode;
  handleLabel: string;
  /**
   * Whether a focused field takes the sheet to `large` (default). Off where
   * the layout itself shrinks above the keyboard instead.
   */
  growOnFocus?: boolean;
  /** Share of the body at the medium height (default half). */
  mediumRatio?: number;
  /** Duration/easing classes for height changes not made by dragging. */
  heightTransitionClassName?: string;
}

const SNAP_INDEX: Record<SheetSnap, number> = {
  collapsed: 0,
  medium: 1,
  large: 2,
};

/** Movement under this many px is a tap, not a drag. */
const DRAG_THRESHOLD_PX = 4;
/** Floor of the velocity window, so a fast tiny move is not read as a flick. */
const MIN_VELOCITY_WINDOW_MS = 50;

const EDITABLE_SELECTOR = 'input, textarea, [contenteditable="true"]';

interface DragState {
  startY: number;
  startHeight: number;
  startTime: number;
  lastY: number;
  moved: boolean;
}

/**
 * Non-modal bottom panel that lives in the editor layout (AD-029), with three
 * heights. The canvas stays visible above it.
 */
export function EditorSheet({
  snap,
  onSnapChange,
  bodyHeight,
  header,
  children,
  handleLabel,
  growOnFocus = true,
  mediumRatio,
  heightTransitionClassName,
}: EditorSheetProps) {
  const { LL } = useI18nContext();
  const heights = getSnapHeights(bodyHeight, mediumRatio);
  // Explicit calls, so the unused-token scan sees each key.
  const snapLabels: Record<SheetSnap, string> = {
    collapsed: LL.appEditor.sheet.snap.collapsed(),
    medium: LL.appEditor.sheet.snap.medium(),
    large: LL.appEditor.sheet.snap.large(),
  };
  const [dragHeight, setDragHeight] = useState<number | null>(null);
  const drag = useRef<DragState | null>(null);
  // The click that ends a drag must not count as a handle tap.
  const suppressClick = useRef(false);

  const clamp = (px: number) =>
    Math.min(heights.large, Math.max(heights.collapsed, px));

  const onPointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    drag.current = {
      startY: e.clientY,
      startHeight: heights[snap],
      startTime: e.timeStamp,
      lastY: e.clientY,
      moved: false,
    };
    suppressClick.current = false;
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    const state = drag.current;
    if (!state) return;
    const delta = state.startY - e.clientY;
    if (!state.moved && Math.abs(delta) < DRAG_THRESHOLD_PX) return;
    state.moved = true;
    state.lastY = e.clientY;
    setDragHeight(clamp(state.startHeight + delta));
  };

  const onPointerUp = (e: React.PointerEvent<HTMLButtonElement>) => {
    const state = drag.current;
    drag.current = null;
    if (!state?.moved) return;
    suppressClick.current = true;
    const delta = state.startY - state.lastY;
    const elapsed = Math.max(
      e.timeStamp - state.startTime,
      MIN_VELOCITY_WINDOW_MS
    );
    setDragHeight(null);
    onSnapChange(
      nearestSnap(
        clamp(state.startHeight + delta),
        bodyHeight,
        delta / elapsed,
        mediumRatio
      )
    );
  };

  const onClick = () => {
    if (suppressClick.current) {
      suppressClick.current = false;
      return;
    }
    onSnapChange(nextSnapOnHandleTap(snap));
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
    e.preventDefault();
    onSnapChange(stepSnap(snap, e.key === 'ArrowUp' ? 'up' : 'down'));
  };

  // `focusin` bubbles through React's onFocus. The virtual keyboard hides the
  // lower half of the screen, so a focused field gets the large height.
  const onFocus = (e: React.FocusEvent<HTMLElement>) => {
    const target = e.target;
    if (!growOnFocus || !target.matches(EDITABLE_SELECTOR)) return;
    onSnapChange('large');
    requestAnimationFrame(() => {
      target.scrollIntoView?.({ block: 'center' });
    });
  };

  return (
    <section
      data-snap={snap}
      style={{ height: dragHeight ?? heights[snap] }}
      onFocus={onFocus}
      className={cn(
        'flex flex-col border-t bg-background pb-[env(safe-area-inset-bottom)] max-md:rounded-t-mobile-sheet',
        dragHeight === null &&
          cn(
            'transition-[height] motion-reduce:transition-none',
            heightTransitionClassName
          )
      )}
    >
      {/* SPEC_DEVIATION: role="slider" on the handle button. Reason:
          aria-valuetext is only valid on range roles; a bare button ignores it. */}
      <button
        type="button"
        role="slider"
        data-editor-sheet-handle
        aria-label={handleLabel}
        aria-orientation="vertical"
        aria-valuemin={0}
        aria-valuemax={2}
        aria-valuenow={SNAP_INDEX[snap]}
        aria-valuetext={snapLabels[snap]}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onClick={onClick}
        onKeyDown={onKeyDown}
        className="mx-auto flex h-6 w-12 shrink-0 touch-none items-center justify-center"
      >
        <span className="h-1.5 w-9 rounded-full bg-muted-foreground/40" />
      </button>
      {header}
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        {children}
      </div>
    </section>
  );
}
